/**
 * Escalation Manager - Complete UI for Escalation governance type
 * 
 * Manages timeout handling and exception paths
 * Part of the 15 fundamental workflow types system
 */

import { gql, useMutation, useQuery } from '@ferp/graphql-client';
import {
    ActionIcon,
    Alert,
    Badge,
    Button,
    Card,
    Code,
    Group,
    JsonInput,
    Menu,
    Modal,
    NumberInput,
    Stack,
    Stepper,
    Switch,
    Tabs,
    Text,
    TextInput,
    Textarea,
    ThemeIcon,
    Timeline,
    Title
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
    IconAlertTriangle,
    IconArrowUp,
    IconBell,
    IconCheck,
    IconClock,
    IconCopy,
    IconDots,
    IconPencil,
    IconPlayerPlay,
    IconPlus,
    IconRoute,
    IconSettings,
    IconTrash,
    IconUserUp,
    IconUsers,
} from '@tabler/icons-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { Escalation } from '../types-16-complete';
import { useGenerateId } from '../hooks/useGenerateId';
import { EntityType } from '../types/EntityType';

interface EscalationManagerProps {
  tenant: string;
  onEscalationChange?: (escalations: Escalation[]) => void;
  readonly?: boolean;
}

interface EscalationExecution {
  triggeredAt: Date;
  currentStep: number;
  assignee: string;
  reason: string;
  resolved: boolean;
}

export const EscalationManager: React.FC<EscalationManagerProps> = ({
  tenant,
  onEscalationChange,
  readonly = false,
}) => {
  const [escalations, setEscalations] = useState<Escalation[]>([]);
  const [selectedEscalation, setSelectedEscalation] = useState<Escalation | null>(null);
  const [editingEscalation, setEditingEscalation] = useState<Partial<Escalation> | null>(null);
  const [loading, setLoading] = useState(false);
  const [executions, setExecutions] = useState<Record<string, EscalationExecution[]>>({});
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [formErrors, setFormErrors] = useState<Record<string, string | undefined>>({});

  // FERP 5-layer enforcement: Backend ID generation only
  const { generateId } = useGenerateId();

  // All escalation data now comes from backend APIs - no hardcoded data

  // All execution data now comes from backend APIs - no hardcoded data

  // GraphQL wiring
  const GET_ESCALATIONS = gql`
    query GetEscalations($tenant: String!) {
      escalations(tenant: $tenant) {
        id
        name
        triggerCondition
        timeoutMs
        escalationPath
        autoReassign
        metadata
      }
    }
  `;
  const UPSERT_ESCALATION = gql`
    mutation UpsertEscalation($tenant: String!, $escalation: EscalationInput!) {
      upsertEscalation(tenant: $tenant, escalation: $escalation) { id }
    }
  `;
  const DELETE_ESCALATION = gql`
    mutation DeleteEscalation($tenant: String!, $id: ID!) { deleteEscalation(tenant: $tenant, id: $id) }
  `;
  const TEST_ESCALATION = gql`
    mutation TestEscalation($tenant: String!, $id: ID!) {
      testEscalation(tenant: $tenant, id: $id) {
        triggeredAt
        currentStep
        assignee
        reason
        resolved
      }
    }
  `;

  const { data, refetch } = useQuery<Record<string, any>>(GET_ESCALATIONS, { variables: { tenant } } as any);
  const [mutateUpsert] = useMutation<Record<string, any>>(UPSERT_ESCALATION);
  const [mutateDelete] = useMutation<Record<string, any>>(DELETE_ESCALATION);
  const [mutateTest] = useMutation<Record<string, any>>(TEST_ESCALATION);

  useEffect(() => {
    if (data?.escalations) {
      setEscalations(data.escalations as any);
    }
    // Execution data should come from backend APIs
    // TODO: Implement escalation execution status API
  }, [data]);

  const createNewEscalation = useCallback(() => {
    const newEscalation: Partial<Escalation> = {
      name: '',
      triggerCondition: '',
      timeoutMs: 3600000, // 1 hour default
      escalationPath: [],
      autoReassign: false,
      metadata: {},
    };
    setEditingEscalation(newEscalation);
    setSelectedEscalation(null);
    openModal();
  }, [openModal]);

  const editEscalation = useCallback((escalation: Escalation) => {
    setEditingEscalation({ ...escalation });
    setSelectedEscalation(escalation);
    openModal();
  }, [openModal]);

  const saveEscalation = useCallback(async () => {
    if (!editingEscalation || !editingEscalation.name) return;
    
    setLoading(true);
    try {
      const errs: string[] = [];
      if (!editingEscalation.name) errs.push('Name is required');
      if (!editingEscalation.timeoutMs || editingEscalation.timeoutMs < 60000) errs.push('Timeout must be >= 60,000ms');
      if ((editingEscalation.escalationPath || []).length === 0) errs.push('Escalation path must have at least one step');
      if (errs.length > 0) {
        setFormErrors({
          name: !editingEscalation.name ? 'Required' : undefined,
          timeout: !editingEscalation.timeoutMs || editingEscalation.timeoutMs < 60000 ? '>= 60000' : undefined,
          path: (editingEscalation.escalationPath || []).length === 0 ? 'Add at least one step' : undefined,
        });
        notifications.show({ title: 'Validation error', message: errs.join('; '), color: 'red' });
        return;
      }

      // FERP architecture: Backend ID generation (5-layer enforcement)
      const escalationId = editingEscalation.id || await generateId(EntityType.escalation);

      const escalationToSave: Escalation = {
        id: escalationId,
        name: editingEscalation.name,
        triggerCondition: editingEscalation.triggerCondition || '',
        timeoutMs: editingEscalation.timeoutMs || 3600000,
        escalationPath: editingEscalation.escalationPath || [],
        autoReassign: editingEscalation.autoReassign || false,
        metadata: editingEscalation.metadata || {},
      };

      const updatedEscalations = selectedEscalation
        ? escalations.map(e => e.id === selectedEscalation.id ? escalationToSave : e)
        : [...escalations, escalationToSave];

      await mutateUpsert({ variables: { tenant, escalation: escalationToSave } });
      await refetch?.();
      setEscalations(updatedEscalations);
      onEscalationChange?.(updatedEscalations);
      
      notifications.show({
        title: 'Success',
        message: `Escalation "${escalationToSave.name}" ${selectedEscalation ? 'updated' : 'created'} successfully`,
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      
      closeModal();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to save escalation',
        color: 'red',
        icon: <IconAlertTriangle size={16} />,
      });
    } finally {
      setLoading(false);
    }
  }, [editingEscalation, selectedEscalation, escalations, onEscalationChange, closeModal]);

  const deleteEscalation = useCallback(async (escalationId: string) => {
    setLoading(true);
    try {
      await mutateDelete({ variables: { tenant, id: escalationId } });
      await refetch?.();
      const updatedEscalations = escalations.filter(e => e.id !== escalationId);
      setEscalations(updatedEscalations);
      const newExecutions = { ...executions };
      delete newExecutions[escalationId];
      setExecutions(newExecutions);
      onEscalationChange?.(updatedEscalations);
      notifications.show({ title: 'Escalation Deleted', message: 'Escalation has been removed', color: 'orange' });
    } finally {
      setLoading(false);
    }
  }, [escalations, executions, onEscalationChange, mutateDelete, tenant, refetch]);

  const testEscalation = useCallback(async (escalation: Escalation) => {
    setLoading(true);
    try {
      const res = await mutateTest({ variables: { tenant, id: escalation.id } });
      const te = res?.data?.testEscalation;
      if (te) {
        const testExecution: EscalationExecution = {
          triggeredAt: new Date(te.triggeredAt || Date.now()),
          currentStep: te.currentStep ?? 0,
          assignee: te.assignee || (escalation.escalationPath[0] || 'unknown'),
          reason: te.reason || 'Test escalation execution',
          resolved: !!te.resolved,
        };
        setExecutions(prev => ({ ...prev, [escalation.id]: [...(prev[escalation.id] || []), testExecution] }));
        notifications.show({ title: 'Escalation Triggered', message: `Test escalation sent to ${testExecution.assignee}`, color: 'blue', icon: <IconBell size={16} /> });
      }
    } finally {
      setLoading(false);
    }
  }, [mutateTest, tenant]);

  const formatTimeout = (timeoutMs: number) => {
    const hours = Math.floor(timeoutMs / (1000 * 60 * 60));
    const minutes = Math.floor((timeoutMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h`;
    if (minutes > 0) return `${minutes}m`;
    return `${Math.floor(timeoutMs / 1000)}s`;
  };

  const getSeverityColor = (severity?: string) => {
    const colors: Record<string, string> = {
      critical: 'red',
      high: 'orange',
      medium: 'yellow',
      low: 'blue',
    };
    return colors[severity || ''] || 'gray';
  };

  const getCategoryIcon = (category?: string) => {
    const icons: Record<string, any> = {
      patientSafety: IconAlertTriangle,
      financial: IconClock,
      technical: IconSettings,
      compliance: IconUsers,
    };
    return icons[category || ''] || IconRoute;
  };

  return (
    <Stack gap="md">
      <Group justify="apart">
        <div>
          <Title order={3}>
            <Group gap="xs">
              <IconUserUp size={24} />
              Escalation Management
            </Group>
          </Title>
          <Text size="sm" color="dimmed">
            Timeout handling and exception paths ({escalations.length} escalations)
          </Text>
        </div>
        {!readonly && (
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={createNewEscalation}
            variant="filled"
          >
            New Escalation
          </Button>
        )}
      </Group>

      {escalations.length === 0 ? (
        <Alert icon={<IconUserUp size={16} />} title="No Escalations Configured">
          <Text size="sm">
            No escalations have been configured yet. Click &quot;New Escalation&quot; to create your first timeout handling policy.
          </Text>
        </Alert>
      ) : (
        <Stack gap="sm">
          {escalations.map((escalation) => {
            const CategoryIcon = getCategoryIcon(escalation.metadata?.category);
            const activeExecutions = executions[escalation.id]?.filter(e => !e.resolved) || [];
            const totalExecutions = executions[escalation.id]?.length || 0;
            
            return (
              <Card key={escalation.id} shadow="sm" padding="md" withBorder>
                <Group justify="apart" ta="left">
                  <div style={{ flex: 1 }}>
                    <Group gap="xs" ta="center">
                      <ThemeIcon size="sm" variant="light" color="orange">
                        <CategoryIcon size={16} />
                      </ThemeIcon>
                      <Text fw={600}>{escalation.name}</Text>
                      <Badge 
                        color={getSeverityColor(escalation.metadata?.severity)}
                        variant="light"
                        size="sm"
                      >
                        {escalation.metadata?.severity?.toUpperCase() || 'NORMAL'}
                      </Badge>
                      {escalation.autoReassign && (
                        <Badge
                          color="blue"
                          variant="outline"
                          size="xs"
                        >
                          Auto-reassign
                        </Badge>
                      )}
                      {activeExecutions.length > 0 && (
                        <Badge 
                          color="red"
                          variant="filled"
                          size="xs"
                          leftSection={<IconBell size={10} />}
                        >
                          {activeExecutions.length} Active
                        </Badge>
                      )}
                    </Group>
                    
                    <Group gap="lg" mt="xs">
                      <Group gap="xs">
                        <IconClock size={14} color="gray" />
                        <Text size="sm" color="dimmed">
                          {formatTimeout(escalation.timeoutMs)}
                        </Text>
                      </Group>
                      <Group gap="xs">
                        <IconRoute size={14} color="gray" />
                        <Text size="sm" color="dimmed">
                          {escalation.escalationPath.length} step{escalation.escalationPath.length !== 1 ? 's' : ''}
                        </Text>
                      </Group>
                      {escalation.metadata?.category && (
                        <Badge color="gray" variant="outline" size="xs">
                          {escalation.metadata.category.replace('_', ' ')}
                        </Badge>
                      )}
                    </Group>
                    
                    <Code block mt="xs" style={{ backgroundColor: '#f8f9fa' }}>
                      {escalation.triggerCondition}
                    </Code>
                    
                    {totalExecutions > 0 && (
                      <Group gap="xs" mt="xs">
                        <Text size="xs" color="dimmed">
                          Total executions: {totalExecutions}
                        </Text>
                        {activeExecutions.length > 0 && (
                          <Text size="xs" color="red" fw={500}>
                            • {activeExecutions.length} currently active
                          </Text>
                        )}
                      </Group>
                    )}
                    
                    {activeExecutions.length > 0 && (
                      <Timeline active={-1} lineWidth={1} mt="sm">
                        {activeExecutions.slice(0, 2).map((execution, index) => (
                          <Timeline.Item 
                            key={index}
                            bullet={
                              <ThemeIcon size={16} radius="xl" color="red" variant="filled">
                                <IconBell size={10} />
                              </ThemeIcon>
                            }
                            title={
                              <Text size="xs" fw={500}>
                                Step {execution.currentStep + 1}: {execution.assignee}
                              </Text>
                            }
                          >
                            <Text size="xs" color="dimmed">
                              {execution.reason}
                            </Text>
                            <Text size="xs" color="dimmed">
                              {execution.triggeredAt.toLocaleString()}
                            </Text>
                          </Timeline.Item>
                        ))}
                      </Timeline>
                    )}
                  </div>
                  
                  {!readonly && (
                    <Menu shadow="md" width={200}>
                      <Menu.Target>
                        <ActionIcon variant="subtle">
                          <IconDots size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      
                      <Menu.Dropdown>
                        <Menu.Item 
                          leftSection={<IconPencil size={14} />}
                          onClick={() => editEscalation(escalation)}
                        >
                          Edit Escalation
                        </Menu.Item>
                        <Menu.Item 
                          leftSection={<IconPlayerPlay size={14} />}
                          onClick={() => testEscalation(escalation)}
                          disabled={loading}
                        >
                          Test Escalation
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconCopy size={14} />}
                          onClick={async () => {
                            // FERP architecture: Backend ID generation (5-layer enforcement)
                            const duplicateId = await generateId(EntityType.escalation);
                            const duplicate = {
                              ...escalation,
                              id: duplicateId,
                              name: `${escalation.name} (Copy)`
                            };
                            setEscalations([...escalations, duplicate]);
                          }}
                        >
                          Duplicate
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item 
                          leftSection={<IconTrash size={14} />}
                          color="red"
                          onClick={() => deleteEscalation(escalation.id)}
                        >
                          Delete
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  )}
                </Group>
              </Card>
            );
          })}
        </Stack>
      )}

      <Modal
        opened={modalOpened}
        onClose={closeModal}
        title={selectedEscalation ? 'Edit Escalation' : 'Create New Escalation'}
        size="lg"
        overlayProps={{ opacity: 0.55, blur: 3 }}
      >
        {editingEscalation && (
          <Tabs defaultValue="basic" variant="outline">
            <Tabs.List>
              <Tabs.Tab value="basic" leftSection={<IconSettings size={16} />}>
                Basic Info
              </Tabs.Tab>
              <Tabs.Tab value="path" leftSection={<IconRoute size={16} />}>
                Escalation Path
              </Tabs.Tab>
              <Tabs.Tab value="trigger" leftSection={<IconClock size={16} />}>
                Trigger Conditions
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="basic" pt="md">
              <Stack gap="md">
                <TextInput
                  label="Escalation Name"
                  placeholder="Enter escalation name"
                  required
                  value={editingEscalation.name || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingEscalation({ ...editingEscalation, name: e.target.value })}
                  error={formErrors['name']}
                />
                
                <NumberInput
                  label="Timeout (milliseconds)"
                  placeholder="3600000"
                  value={editingEscalation.timeoutMs}
                  onChange={(value: number | string | null) => setEditingEscalation({ ...editingEscalation, timeoutMs: (typeof value === 'number' ? value : 3600000) })}
                  min={60000} // 1 minute minimum
                  max={86400000} // 24 hours maximum
                  step={300000} // 5 minute steps
                  description={`Current timeout: ${formatTimeout(editingEscalation.timeoutMs || 3600000)}`}
                  error={formErrors['timeout']}
                />
                
                <Group>
                  <Switch
                    label="Auto-reassign"
                    description="Automatically reassign to next person in escalation path"
                    checked={editingEscalation.autoReassign || false}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingEscalation({
                      ...editingEscalation,
                      autoReassign: e.currentTarget.checked
                    })}
                  />
                </Group>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="path" pt="md">
              <Stack gap="md">
                <Alert icon={<IconArrowUp size={16} />} color="blue">
                  Define the escalation path order. Each step represents who should be notified next.
                </Alert>
                
                <div>
                  <Text size="sm" fw={500} mb="sm">Current Escalation Path:</Text>
                  {editingEscalation.escalationPath && editingEscalation.escalationPath.length > 0 ? (
                    <Stepper active={editingEscalation.escalationPath.length} orientation={'vertical' as const} size="sm">
                      {editingEscalation.escalationPath.map((step: string, index: number) => (
                        <Stepper.Step
                          key={index}
                          label={`Step ${index + 1}`}
                          description={
                            <Group justify="apart">
                              <Text size="sm">{step}</Text>
                              <ActionIcon
                                color="red"
                                variant="subtle"
                                size="sm"
                                onClick={() => {
                                  const newPath = editingEscalation.escalationPath?.filter((_: string, i: number) => i !== index) || [];
                                  setEditingEscalation({ ...editingEscalation, escalationPath: newPath });
                                }}
                              >
                                <IconTrash size={14} />
                              </ActionIcon>
                            </Group>
                          }
                        />
                      ))}
                    </Stepper>
                  ) : (
                    <Text size="sm" color="dimmed">No escalation steps defined</Text>
                  )}
                  {formErrors['path'] && (
                    <Text size="xs" color="red">{formErrors['path']}</Text>
                  )}
                </div>
                
                <Card withBorder padding="sm">
                  <Text size="sm" fw={500} mb="sm">Add Escalation Step</Text>
                  <Group>
                    <TextInput
                      placeholder="Role or person (e.g., team_lead, manager)"
                      style={{ flex: 1 }}
                      id="new-escalation-step"
                    />
                    <Button
                      size="sm"
                      variant="light"
                      leftSection={<IconPlus size={14} />}
                      onClick={() => {
                        const stepEl = document.getElementById('new-escalation-step') as HTMLInputElement;
                        if (stepEl?.value) {
                          setEditingEscalation({
                            ...editingEscalation,
                            escalationPath: [...(editingEscalation.escalationPath || []), stepEl.value],
                          });
                          stepEl.value = '';
                        }
                      }}
                    >
                      Add Step
                    </Button>
                  </Group>
                </Card>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="trigger" pt="md">
              <Stack gap="md">
                <Textarea
                  label="Trigger Condition"
                  placeholder="task.priority == 'critical' && task.age_hours > 2"
                  value={editingEscalation.triggerCondition || ''}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditingEscalation({ ...editingEscalation, triggerCondition: e.target.value })}
                  minRows={3}
                  description="Boolean expression that triggers the escalation"
                />
                
                <Alert icon={<IconClock size={16} />} color="yellow">
                  <Text size="sm" fw={500} mb="xs">Common Trigger Patterns:</Text>
                  <Stack gap="xs">
                    <Code>task.age_minutes {'>'} 30</Code>
                    <Code>payment.status == &quot;failed&quot; {'&&'} payment.retry_count {'>='} 3</Code>
                    <Code>integration.downtime_minutes {'>'} 15</Code>
                    <Code>audit.due_date {'<'} now() + 24h</Code>
                  </Stack>
                </Alert>
                
                <JsonInput
                  label="Metadata"
                  placeholder="Enter metadata JSON"
                  value={JSON.stringify(editingEscalation.metadata || {}, null, 2)}
                  onChange={(value) => {
                    try {
                      setEditingEscalation({ ...editingEscalation, metadata: JSON.parse(value) });
                    } catch (e) {
                      // Invalid JSON, ignore
                    }
                  }}
                  minRows={4}
                />
              </Stack>
            </Tabs.Panel>
          </Tabs>
        )}
        
        <Group justify="apart" mt="md">
          <Button variant="light" onClick={closeModal}>
            Cancel
          </Button>
          
          <Button
            onClick={saveEscalation}
            loading={loading}
            leftSection={<IconCheck size={16} />}
          >
            {selectedEscalation ? 'Update' : 'Create'} Escalation
          </Button>
        </Group>
      </Modal>
    </Stack>
  );
};

export default EscalationManager;